/**
 * Studio Plaz · Trust Bundle Verifier — public drag-drop chain-of-custody check.
 *
 * Route: /trust/verify
 *
 * Recipients (attorneys, VCs, grant reviewers, enterprise procurement) who
 * received a Filings Bundle (.zip) drag-drop it here. The page computes
 * SHA-256 of every embedded doc, compares to the manifest, and emits an
 * independent verdict — turning the bundle into a two-sided trust handshake.
 *
 * No authentication. The bundle itself carries the proof.
 */
import { useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Frame({ children }) {
  return (
    <div
      data-testid="trust-verify-frame"
      style={{
        minHeight: "100vh",
        background: "#fafafa",
        color: "#0b0f17",
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Inter", sans-serif',
        fontSize: 16,
        lineHeight: 1.65,
      }}
    >
      <header
        style={{
          maxWidth: 920,
          margin: "0 auto",
          padding: "32px 28px 18px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <a href="/trust" style={{ color: "#0b0f17", fontWeight: 600, textDecoration: "none", fontSize: 18 }}>
          Studio Plaz · Trust Center
        </a>
        <span style={{ fontSize: 12, color: "#6b7280", letterSpacing: "0.04em", textTransform: "uppercase" }}>
          Bundle Verifier
        </span>
      </header>
      <main style={{ maxWidth: 920, margin: "0 auto", padding: "36px 28px 80px" }}>{children}</main>
      <footer
        style={{
          maxWidth: 920,
          margin: "0 auto",
          padding: "16px 28px 36px",
          fontSize: 12,
          color: "#9ca3af",
        }}
      >
        Every verification attempt is logged for our compliance records.
      </footer>
    </div>
  );
}

function VerdictBanner({ verdict }) {
  const map = {
    verified:  { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", label: "Verified",   icon: "✓" },
    tampered:  { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", label: "Tampered",   icon: "✕" },
    unsigned:  { color: "#d97706", bg: "#fffbeb", border: "#fde68a", label: "Unsigned",   icon: "!" },
    corrupt:   { color: "#6b7280", bg: "#f3f4f6", border: "#d1d5db", label: "Corrupt",    icon: "?" },
  };
  const s = map[verdict] || map.corrupt;
  return (
    <div
      data-testid={`trust-verify-verdict-${verdict}`}
      style={{
        background: s.bg,
        border: `1px solid ${s.border}`,
        borderRadius: 12,
        padding: "20px 24px",
        marginBottom: 24,
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}
    >
      <div
        style={{
          width: 38, height: 38, borderRadius: 999,
          background: s.color, color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 700, fontSize: 20,
        }}
      >
        {s.icon}
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: s.color }}>
          Verdict
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#0b0f17" }}>{s.label}</div>
      </div>
    </div>
  );
}

function StatRow({ label, value, mono }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 16, padding: "8px 0", borderTop: "1px solid #f3f4f6" }}>
      <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      <div style={{ fontSize: 13, color: "#0b0f17", fontFamily: mono ? "ui-monospace, SFMono-Regular, monospace" : "inherit", wordBreak: "break-all" }}>
        {value || "—"}
      </div>
    </div>
  );
}

function ReceiptForm({ file, bundleId }) {
  const [email, setEmail] = useState("");
  const [org, setOrg] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !org.trim()) {
      setError("Email and organization are required.");
      return;
    }
    if (!file) {
      setError("Re-upload the bundle to request a receipt.");
      return;
    }
    setError(null);
    setRequesting(true);
    setReceipt(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams({
        recipient_email: email.trim(),
        recipient_organization: org.trim(),
      }).toString();
      const r = await axios.post(`${API}/trust/verify-receipt?${params}`, fd);
      setReceipt(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Receipt request failed.");
    } finally {
      setRequesting(false);
    }
  };

  if (receipt) {
    return (
      <div
        data-testid="trust-verify-receipt-issued"
        style={{
          background: "#f0fdf4",
          border: "1px solid #bbf7d0",
          borderRadius: 12,
          padding: "20px 24px",
          marginBottom: 24,
        }}
      >
        <h2 style={{ fontSize: 12, fontWeight: 700, color: "#15803d", margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Verification Receipt issued
        </h2>
        <p style={{ fontSize: 13, color: "#0b0f17", margin: "0 0 10px" }}>
          A signed PDF receipt has been generated for bundle{" "}
          <strong style={{ fontFamily: "ui-monospace, monospace" }}>{receipt.bundle_id}</strong>.
        </p>
        <StatRow label="Receipt ID" value={receipt.receipt_id} mono />
        <StatRow label="Verdict" value={receipt.verdict} />
        <StatRow label="PDF size" value={`${receipt.pdf_size_bytes} bytes`} />
        <StatRow
          label="Email delivery"
          value={
            receipt.email?.sent
              ? "delivered via Resend" + (receipt.email?.attachment_skipped ? " (attachment skipped in dev mode)" : "")
              : `not sent · ${receipt.email?.reason || "unknown"}`
          }
        />
        <details style={{ marginTop: 12, fontSize: 12 }}>
          <summary style={{ cursor: "pointer", fontWeight: 600, color: "#15803d" }}>
            Show RS256 receipt JWS (independently verifiable)
          </summary>
          <textarea
            readOnly
            value={receipt.receipt_jws}
            style={{
              width: "100%",
              marginTop: 8,
              fontFamily: "ui-monospace, monospace",
              fontSize: 11,
              padding: 8,
              border: "1px solid #bbf7d0",
              borderRadius: 6,
              minHeight: 80,
              background: "#fff",
              color: "#0b0f17",
            }}
            data-testid="trust-verify-receipt-jws"
          />
          <p style={{ marginTop: 6, color: "#4b5563" }}>
            Decode this JWS against <code>/api/trust/jwks-public-pem</code> to
            independently verify the receipt.
          </p>
        </details>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="trust-verify-receipt-form"
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: "20px 24px",
        marginBottom: 24,
      }}
    >
      <h2 style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6b7280", margin: "0 0 4px" }}>
        Request a signed receipt (optional)
      </h2>
      <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 14px" }}>
        Receive a 1-page PDF with the verification verdict, bundle metadata,
        and an embedded RS256 signature. Useful for compliance files,
        diligence checklists, and grant-review paper trails.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <input
          type="email"
          required
          placeholder="your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="trust-verify-receipt-email"
          style={{ padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13 }}
        />
        <input
          type="text"
          required
          placeholder="your organization"
          value={org}
          onChange={(e) => setOrg(e.target.value)}
          data-testid="trust-verify-receipt-org"
          style={{ padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13 }}
        />
      </div>
      <button
        type="submit"
        disabled={requesting}
        data-testid="trust-verify-receipt-submit"
        style={{
          padding: "10px 16px",
          background: "#0b0f17",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          fontWeight: 600,
          fontSize: 13,
          cursor: requesting ? "not-allowed" : "pointer",
          opacity: requesting ? 0.5 : 1,
        }}
      >
        {requesting ? "Generating receipt…" : "Email me the signed receipt"}
      </button>
      {error && (
        <div style={{ marginTop: 10, color: "#b91c1c", fontSize: 12 }} data-testid="trust-verify-receipt-error">{error}</div>
      )}
    </form>
  );
}

export default function TrustBundleVerify() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      setError("Please upload a .zip bundle.");
      return;
    }
    setFile(f);
    setResult(null);
    setError(null);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${API}/trust/verify-bundle`, fd);
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  return (
    <Frame>
      <h1 style={{ fontSize: 32, fontWeight: 700, margin: "0 0 8px", letterSpacing: "-0.01em" }}>
        Verify a Filings Bundle
      </h1>
      <p style={{ color: "#4b5563", margin: "0 0 28px", maxWidth: 640 }}>
        Drop a Studio Plaz Filings Bundle (.zip) below. We will recompute
        SHA-256 of every embedded document, compare to the manifest, and
        emit an independent verdict. No upload to disk — bytes are
        verified in-memory and discarded.
      </p>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="trust-verify-drop-zone"
        style={{
          border: `2px dashed ${dragOver ? "#0b0f17" : "#d1d5db"}`,
          background: dragOver ? "#f9fafb" : "#fff",
          borderRadius: 14,
          padding: "44px 28px",
          textAlign: "center",
          cursor: "pointer",
          transition: "all 0.12s ease",
          marginBottom: 24,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, color: "#0b0f17", marginBottom: 6 }}>
          {file ? file.name : "Drag and drop a bundle.zip, or click to choose"}
        </div>
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          {loading ? "Verifying…" : "Max 50 MB · SHA-256 chain check · governance snapshot check"}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".zip"
          style={{ display: "none" }}
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="trust-verify-file-input"
        />
      </div>

      {error && (
        <div
          data-testid="trust-verify-error"
          style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#b91c1c",
            padding: "12px 16px",
            borderRadius: 10,
            marginBottom: 24,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {result && (
        <div data-testid="trust-verify-result">
          <VerdictBanner verdict={result.verdict} />

          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: "20px 24px",
              marginBottom: 24,
            }}
          >
            <h2 style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6b7280", margin: "0 0 8px" }}>
              Bundle
            </h2>
            <StatRow label="Bundle ID" value={result.bundle_id} mono />
            <StatRow label="Profile" value={result.profile_title || result.profile_slug} />
            <StatRow label="Generated at" value={result.generated_at} />
            <StatRow label="Generated by" value={result.generated_by} />
            <StatRow label="Git commit" value={result.git_commit} mono />
            <StatRow label="Bundle SHA-256" value={result.bundle_sha256} mono />
          </div>

          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: "20px 24px",
              marginBottom: 24,
            }}
          >
            <h2 style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6b7280", margin: "0 0 8px" }}>
              Chain of custody
            </h2>
            <StatRow label="Documents declared" value={result.document_count} />
            <StatRow label="Hashes matched" value={result.hash_check?.matches} />
            <StatRow label="Hashes mismatched" value={result.hash_check?.mismatches?.length || 0} />
            <StatRow label="Documents missing" value={result.hash_check?.missing?.length || 0} />
            <StatRow
              label="Governance snapshot"
              value={
                result.governance_snapshot_present
                  ? (result.governance_clean ? "embedded · 0 blocking failures" : "embedded · WARNINGS present")
                  : "not embedded"
              }
            />
          </div>

          {(result.hash_check?.mismatches?.length > 0 || result.hash_check?.missing?.length > 0) && (
            <div
              data-testid="trust-verify-tamper-detail"
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 12,
                padding: "16px 20px",
                marginBottom: 24,
              }}
            >
              <h2 style={{ fontSize: 12, fontWeight: 700, color: "#b91c1c", margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Tamper detail
              </h2>
              {result.hash_check.mismatches.map((m) => (
                <div key={m.path} style={{ fontSize: 12, fontFamily: "ui-monospace, monospace", color: "#7f1d1d", padding: "4px 0", wordBreak: "break-all" }}>
                  ≠ {m.path}
                </div>
              ))}
              {result.hash_check.missing.map((p) => (
                <div key={p} style={{ fontSize: 12, fontFamily: "ui-monospace, monospace", color: "#7f1d1d", padding: "4px 0" }}>
                  ⌀ {p} (missing)
                </div>
              ))}
            </div>
          )}

          {/* Phase F3 · Verification Receipt request form */}
          {result.bundle_id && (
            <ReceiptForm file={file} bundleId={result.bundle_id} />
          )}
        </div>
      )}

      <details style={{ marginTop: 32, fontSize: 13, color: "#4b5563" }}>
        <summary style={{ cursor: "pointer", fontWeight: 600, color: "#0b0f17" }}>
          What does verification prove?
        </summary>
        <div style={{ marginTop: 12, lineHeight: 1.7 }}>
          <p>
            A <strong>Verified</strong> verdict means: every document inside this
            bundle has a SHA-256 hash that matches the manifest. The bundle has
            not been edited, swapped, truncated, or repackaged.
          </p>
          <p>
            A <strong>Tampered</strong> verdict means: at least one document's
            hash does not match. Treat the entire bundle as untrusted.
          </p>
          <p>
            An <strong>Unsigned</strong> verdict means: no manifest is present
            — this zip was not produced by Studio Plaz's institutional export
            system.
          </p>
          <p style={{ color: "#6b7280", fontSize: 12 }}>
            Verification is performed entirely on the bytes you uploaded;
            nothing is stored. Verification attempts are logged for compliance
            records, but the bundle contents are not retained.
          </p>
        </div>
      </details>
    </Frame>
  );
}
